<template>
  <div>
    <!-- loading bar -->
    <v-progress-linear v-if="!loaded" color="vct-blue" indeterminate></v-progress-linear>

    <v-container v-else class="invite-candidate" fluid>
      <v-layout justify-center>
        <v-flex xs12 sm8 md6>
          <!-- logo -->
          <img class="mb-30" src="@/assets/logo.png" style="width: 100%" />

          <!-- View Header -->
          <v-toolbar color="vct-blue">
            <v-toolbar-title class="centered card-header">
              Your invitation to undertake a technical assessment!
            </v-toolbar-title>
          </v-toolbar>

          <!-- Content -->
          <v-card tile>
            <v-card-title>
              <div class="headline full-width mb-3">
                Welcome!
              </div>
              <div class="body-1" style="word-break: break-word;">
                <b>{{ candidate.from }}</b> has invited you to undertake a technical assessment with <a class="no-decoration" href="https://www.vetted-cyber-talent.com/" target="_blank">Vetted Cyber Talent</a>. The purpose of this assessment is to evaluate your skills.
              </div>
            </v-card-title>

            <v-divider />

            <!-- Exercises -->
            <v-card-text>
              <h3 class="mb-5">
                Exercises that you've been assigned to complete:
              </h3>

              <v-data-table
                :headers="headers"
                :items="studentExercises"
                :items-per-page="-1"
                class="exercises-table"
                disable-sort
                hide-default-footer
              >
                <template v-slot:item.groupUID="{ item }">
                  {{ formatText(item.groupUID) }}
                </template>
              </v-data-table>
            </v-card-text>

            <v-divider />

            <!-- Accept challenge -->
            <v-card-text class="align-center" style="color: unset;">
              <v-btn v-if="!candidate.process['accepted-challenge']"
                @click="accept()"
                :loading="loading"
                color="vct-blue"
                >

                Accept Challenge!
              </v-btn>

              <!-- Congrats message -->
              <div v-else>
                <h3 class="mb-5">
                  Great! You've accepted the challenge!
                </h3>

                <p>
                  As a next step, create an account or authenticate into MCSI's <a class="no-decoration" href="https://platform.mosse-institute.com/" target="_blank">Online Learning Platform</a>, read the exercises' descriptions and submit your answers!
                </p>
              </div>
            </v-card-text>

            <v-divider />

            <!-- Frequently asked questions -->
            <v-card-text class="body-2 card-text">
              <h2 class="mb-5">
                Frequently Asked Questions
              </h2>

              <!-- question -->
              <h3 class="mb-2">
                What is Vetted Cyber Talent (VCT)?
              </h3>
              <p>
                VCT offers independent vetting services to evaluate the competencies of candidates applying for cybersecurity roles. Each challenge simulates a real-world problem that candidates are expected to solve, ensuring a thorough assessment of their skills and readiness for the job.
              </p>

              <!-- question -->
              <h3 class="mb-2 mt-15">
                How does it work?
              </h3>
              <p>
                <ol type="1">
                  <li>
                    Click the 'Accept Challenge' button
                  </li>

                  <li class="mt-2">
                    Create an account or authenticate into the <a class="no-decoration" href="https://platform.mosse-institute.com/" target="_blank">Online Learning Platform</a> with email address <i>{{ candidate.email }}</i>
                  </li>

                  <li class="mt-2">
                    In the menu, click on 'Career Development'
                  </li>

                  <li class="mt-2">
                    Then, click on 'Assessments'
                  </li>

                  <li class="mt-2">
                    Complete the challenges!
                  </li>
                </ol>
              </p>

              <!-- question -->
              <h3 class="mb-2 mt-15">
                Who will review and mark my submissions?
              </h3>
              <p>
                Professional cyber security instructors that work for <a class="no-decoration" href="https://www.mosse-institute.com/" target="_blank">Mossé Cyber Security Institute</a>.
              </p>

              <!-- question -->
              <h3 class="mb-2 mt-15">
                What happens if I fail a challenge?
              </h3>
              <p>
                You get to try again!
              </p>

              <!-- question -->
              <h3 class="mb-2 mt-15">
                Will my submissions be made available to the organization that is vetting me?
              </h3>
              <p>
                Yes. They will have full access to view everything.
              </p>

              <!-- question -->
              <h3 class="mb-2 mt-15">
                How much time do I have to complete the challenges?
              </h3>
              <p>
                As much time as the person who invited you to undertake these challenges is willing to give you. Contact them to ask!
              </p>
            </v-card-text>
          </v-card>

          <div class="align-center caption mt-5">
            Copyright (c) Vetted Cyber Talent
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import 'firebase/compat/functions'

import axios from 'axios'
import firebase from 'firebase/compat/app'
import firestore from '@/mixins/firestore.js'
import ui from '@/stores/ui.js'

export default {
  mixins: [firestore],

  data () {
    return {
      candidate: null,
      exercises: null,
      loading: false,
      package: null,

      headers: [
        {
          text: 'Exercise',
          value: 'title'
        },
        {
          text: 'Category',
          value: 'groupUID'
        },
        {
          text: 'Difficulty',
          value: 'difficulty'
        }
      ],

      uri: `https://us-central1-cyber-talent-portal.cloudfunctions.net/vettingAcceptChallenge?timestamp=${new Date().getTime()}`
    }
  },

  computed: {
    accepted () {
      return this.$route.params.accepted || null
    },

    candidateId () {
      return this.$route.params.candidateId || null
    },

    loaded () {
      return this.candidateId 
        && !this.loading
        && this.candidate
        && this.package
        && this.exercises
    },

    studentExercises () {
      if (!this.exercises || !this.package || !this.package.exercises) {
        return null
      }

      return this.exercises.filter(e => this.package.exercises.includes(e.uuid))
    }
  },

  methods: {
    /*
     * API call to accept the challenge
     */
    async accept () {
      this.loading = true

      await axios.post(this.uri, {candidateId: this.candidateId})
        .then(() => {
          this.retrieveDocs()
        })
        .catch((error) => {
          window.console.log(error)
          ui.commit('triggerErrorSnackbar', 'An error has happened!')
        })

      this.loading = false
    },

    /*
     * Turns a string like "specialist-bundle-metasploit" into "Specialist Bundle Metasploit"
     */
    formatText (text) {
      if (text.toLowerCase() === 'grc') {
        return 'GRC'
      }
      
      return text
        .split('-').join(' ')
        .replace(/\w\S*/g, function (txt) {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        })
    },

    /*
     * API call to note that the `email` was opened
     */
    async opened () {
      await axios
        .post(`https://us-central1-cyber-talent-portal.cloudfunctions.net/vettingOpenedEmail?candidateId=${this.candidateId}&timestamp=${new Date().getTime()}`,)
        .catch((error) => {
          window.console.log(error)
        })
    },

    /*
     * Retrieve the firestore document
     */
    async retrieveDocs () {
      if (!this.candidateId) {
        return
      }

      this.candidate = null
      this.package   = null

      this.candidate = await this.fsGet('candidates', this.candidateId)

      if (this.candidate && this.candidate.package) {
        this.package = await this.fsGet('vetting-packages', this.candidate.package)
      }
    },

    /*
     * Retrieve the list of exercises available in VCT
     */
    async retrieveExercises () {
      if (!this.candidateId) {
        return
      }

      var call = firebase.functions().httpsCallable('jsonGetExercises?timestamp=' + new Date().getTime())

      await call()
        .then(async (response) => {
          if (response.data) {
            this.exercises = response.data
          } else {
            await ui.commit('triggerErrorSnackbar', 'Could not download the list of exercises')
          }
        })
        .catch(async (error) => {
          await ui.commit('triggerErrorSnackbar', error)
        })
    }
  },

  mounted () {
    this.opened()
    this.retrieveDocs()
    this.retrieveExercises()

    if (this.accepted) {
      this.accept()
    }
  }
}
</script>

<style>
.exercises-table {
  border: 1px solid gray;
}
</style>